/**
 * Utility functions for generating user-friendly permission error messages
 */

export interface PermissionErrorInfo {
  requiredPermission: string;
  userRole?: string;
  action?: string;
}

/**
 * Get a user-friendly message for a permission error
 */
export function getPermissionErrorMessage(info: PermissionErrorInfo): string {
  const { requiredPermission, userRole, action } = info;
  
  const permissionDescriptions: Record<string, string> = {
    'canCreate': 'create new resources',
    'canRead': 'view resources',
    'canUpdate': 'modify existing resources',
    'canDelete': 'delete resources',
    'canManageUsers': 'manage team members and user accounts',
    'canManageProperties': 'manage properties and units',
    'canManageTenants': 'manage tenant information and records',
    'canManagePayments': 'manage payments and financial transactions',
    'canAccessDashboard': 'access the dashboard',
    'canAccessProperties': 'access the properties page',
    'canAccessTenants': 'access the tenants page',
    'canAccessPayments': 'access the payments page',
    'canAccessReports': 'access the reports page',
    'canAccessSettings': 'access system settings',
    'canAccessAnalytics': 'access the analytics page',
  };

  const permissionName = permissionDescriptions[requiredPermission] || requiredPermission;
  const actionText = action ? ` to ${action}` : '';
  const roleText = userRole ? ` Your current role is ${userRole}.` : '';
  
  return `You don't have permission${actionText}. This action requires the "${requiredPermission}" permission.${roleText} Please contact your administrator to grant this permission.`;
}

/**
 * Get a tooltip message for a disabled button/action
 */
export function getPermissionTooltip(requiredPermission: string, action?: string): string {
  const permissionDescriptions: Record<string, string> = {
    'canCreate': 'Create permission required',
    'canRead': 'Read permission required',
    'canUpdate': 'Update permission required',
    'canDelete': 'Delete permission required',
    'canManageUsers': 'Manage Users permission required',
    'canManageProperties': 'Manage Properties permission required',
    'canManageTenants': 'Manage Tenants permission required',
    'canManagePayments': 'Manage Payments permission required',
  };

  const permissionName = permissionDescriptions[requiredPermission] || requiredPermission;
  const actionText = action ? ` to ${action}` : '';
  
  return `${permissionName}${actionText}. Contact your administrator to grant this permission.`;
}
